import { useMemo } from "react";

import * as Yup from "yup";

import { PO_BOX_REGEX } from "../../../../../constants";
import { createForm } from "../../../forms";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore-error TS7016
import finance from "../../../../../finance";
import {
  useL10n,
  type TypeSafeReactLocalization,
} from "../../../../composites";

export type AddressType = {
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  regionCode: string;
  postalCode: string;
};

export type WireDestinationFormType = {
  accountNumber: string;
  routingNumber: string;
  address: AddressType;
  "address.streetAddress"?: string;
  "address.streetAddress2"?: string;
  "address.city"?: string;
  "address.regionCode"?: string;
  "address.postalCode"?: string;
};

const requiredValidation = (l10n: TypeSafeReactLocalization) =>
  l10n.getString("form-wire-destination-validation-required", {}, "Required");

const accountNumberValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string()
    .required(requiredValidation(l10n))
    .max(34, ({ max }) =>
      l10n.getString(
        "form-wire-destination-validation-account-number-max",
        { max },
        `Account number must be at most ${max} characters`,
      ),
    );

const routingNumberValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string()
    .required(requiredValidation(l10n))
    .test(
      "length",
      l10n.getString(
        "form-wire-destination-validation-routing-number-length",
        {},
        "Please enter a valid 9-digit routing number",
      ),
      (val) => val.length === 9,
    )
    .test(
      "valid",
      l10n.getString(
        "form-wire-destination-validation-routing-number-valid",
        {},
        "Please enter a valid 9-digit routing number",
      ),
      (val) => finance.abaRoutingNumberIsValid(val),
    );

const streetAddressValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string()
    .required(requiredValidation(l10n))
    .max(35, ({ max }) =>
      l10n.getString(
        "form-wire-destination-validation-address-max",
        { max },
        `Address must be at most ${max} characters`,
      ),
    );

const streetAddress2Validation = (l10n: TypeSafeReactLocalization) =>
  Yup.string().max(35, ({ max }) =>
    l10n.getString(
      "form-wire-destination-validation-address-2-max",
      { max },
      `Address line 2 must be at most ${max} characters`,
    ),
  );

const cityValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string()
    .required(requiredValidation(l10n))
    .max(17, ({ max }) =>
      l10n.getString(
        "form-wire-destination-validation-city-max",
        { max },
        `City must be at most ${max} characters`,
      ),
    );

const regionCodeValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string().required(requiredValidation(l10n));

const postalCodeValidation = (l10n: TypeSafeReactLocalization) =>
  Yup.string()
    .required(requiredValidation(l10n))
    .max(10, ({ max }) =>
      l10n.getString(
        "form-wire-destination-validation-postal-code-max",
        { max },
        `Postal code must be at most ${max} characters`,
      ),
    );

const getValidationSchema = (l10n: TypeSafeReactLocalization) => {
  return Yup.object().shape({
    accountNumber: accountNumberValidation(l10n),
    routingNumber: routingNumberValidation(l10n),
    address: Yup.object().shape({
      streetAddress: streetAddressValidation(l10n),
      streetAddress2: streetAddress2Validation(l10n),
      city: cityValidation(l10n),
      regionCode: regionCodeValidation(l10n),
      postalCode: postalCodeValidation(l10n),
    }),
  });
};

export const useValidationSchemaNoPoBox = () => {
  const l10n = useL10n();
  return useMemo(
    () =>
      getValidationSchema(l10n)
        .pick(["accountNumber", "routingNumber"])
        .concat(
          Yup.object().shape({
            address: Yup.object().shape({
              streetAddress: streetAddressValidation(l10n).test(
                "pobox",
                l10n.getString(
                  "form-wire-destination-validation-pobox",
                  {},
                  "Address must not be a P.O. Box",
                ),
                (val) => PO_BOX_REGEX.exec(val) === null,
              ),
              streetAddress2: streetAddress2Validation(l10n),
              city: cityValidation(l10n),
              regionCode: regionCodeValidation(l10n),
              postalCode: postalCodeValidation(l10n),
            }),
          }),
        ),
    [l10n],
  );
};

export const wireDestinationInitialValues: WireDestinationFormType = {
  accountNumber: "",
  routingNumber: "",
  address: {
    streetAddress: "",
    streetAddress2: "",
    city: "",
    regionCode: "",
    postalCode: "",
  },
};

export const transformWireApiFieldsToFormFields = ({
  account_number: accountNumber,
  routing_number: routingNumber,
  address: {
    street_address: streetAddress,
    street_address_2: streetAddress2,
    city,
    region_code: regionCode,
    postal_code: postalCode,
  },
}: API.FedwireDestination) => {
  return {
    accountNumber,
    routingNumber,
    address: {
      streetAddress,
      streetAddress2,
      city,
      regionCode,
      postalCode,
    },
  };
};

export const transformWireFormFieldsToApiFields = ({
  accountNumber: account_number,
  routingNumber: routing_number,
  address: {
    streetAddress: street_address,
    streetAddress2: street_address_2,
    city,
    regionCode: region_code,
    postalCode: postal_code,
  },
  recipientId,
}: WireDestinationFormType & { recipientId: API.RecipientId }) => {
  return {
    account_number,
    routing_number,
    address: {
      street_address,
      street_address_2,
      city,
      region_code,
      postal_code,
      country_code: "US",
    },
    recipient: recipientId,
  } as API.CreateUpdateWireDestination.Request;
};

// define form schema and validations
export const WireDestinationForm = createForm({
  initialValues: wireDestinationInitialValues,
  translateValidationSchema: getValidationSchema,
});
